import type { Container } from "pixi.js";
import type { GambleOfferDetails, GambleResponse } from "@slot/client-engine";
import type { GambleView } from "./GambleView";
import type { Log, PresentationTaskRunner } from "@slot/presentation";
import type { GamblePlayerChoice, GamblePresentationPort, LocalizationService, MoneyFormatter } from "@slot/game-shell";
import { OverlayPressBinding } from "@slot/game-shell";

/**
 * Default gamble visual implementation: card-flip style.
 *
 * Implements GamblePresentationPort — swap this class to change the gamble visual (wheel, ladder,
 * etc.) without touching any coordination or engine code. Input: a tap on the collect button or an
 * option button, and a key on the window = collect (the shell's `OverlayPressBinding`: never a
 * modifier, a Ctrl / Alt / Meta chord or a repeat, the same key rule as the shell's panels).
 */
export class CardFlipGamblePresenter implements GamblePresentationPort {
  private readonly panel: GambleView;
  private readonly formatter: MoneyFormatter;
  private readonly localization: LocalizationService;
  private readonly log: Log;
  private readonly runner: PresentationTaskRunner;

  /** Set once the buttons are enabled; a press before that is ignored, not latched. */
  private armed = false;
  private resolveChoice: ((choice: string) => void) | null = null;

  private collectHandler: (() => void) | null = null;
  private readonly pressBinding = new OverlayPressBinding();
  private optionHandlers: Array<{ node: Container; handler: () => void }> = [];

  /** Delay before enabling buttons to prevent accidental taps */
  private readonly armDelayMs = 400;

  /** Hold duration after the flip completes so the player can read the result */
  private readonly resultHoldMs = 800;

  public constructor(inputArgs: {
    panel: GambleView;
    formatter: MoneyFormatter;
    localization: LocalizationService;
    /** The chrome runner — the clock the card flip already rides. */
    runner: PresentationTaskRunner;
    log: Log;
  }) {
    this.panel = inputArgs.panel;
    this.formatter = inputArgs.formatter;
    this.localization = inputArgs.localization;
    this.runner = inputArgs.runner;
    this.log = inputArgs.log;
  }

  public async presentGambleOffer(details: GambleOfferDetails): Promise<GamblePlayerChoice> {
    this.armed = false;
    this.resolveChoice = null;

    const formattedWin = this.formatter.formatMinor(details.currentWinAmountMinor);
    this.panel.setCurrentWin(formattedWin);

    if (details.options.length > 0) {
      const firstMultiplier = details.options[0].multiplier;
      const potentialWin = (BigInt(details.currentWinAmountMinor) * BigInt(firstMultiplier)).toString();
      const formattedPotential = this.formatter.formatMinor(potentialWin);
      this.panel.setPotentialWin(formattedPotential);
    }

    if (details.maxAttemptsRemaining !== undefined) {
      this.panel.setAttemptsText(
        this.localization.t("gamble.attempts_remaining", "{count} attempts remaining", { count: details.maxAttemptsRemaining }),
      );
    }

    this.panel.setCollectEnabled(false);
    this.panel.setOptionsEnabled(false);
    this.panel.setResultVisible(false);
    this.panel.show();

    this.bindInput(details);

    await this.runner.sleep(this.armDelayMs / 1000);
    this.armed = true;
    this.panel.setCollectEnabled(true);
    this.panel.setOptionsEnabled(true);

    this.log(`[CardFlipGamble] offer shown type=${details.gambleType} win=${formattedWin} options=${details.options.length}`);

    const choice = await this.waitForChoice();
    this.armed = false;

    this.panel.setCollectEnabled(false);
    this.panel.setOptionsEnabled(false);
    this.unbindInput();

    if (choice === "collect") {
      this.log("[CardFlipGamble] player chose collect");
      return { action: "collect" };
    }

    this.log(`[CardFlipGamble] player chose gamble option=${choice}`);
    return { action: "gamble", optionId: choice };
  }

  public async presentGambleResult(response: GambleResponse): Promise<void> {
    this.log(`[CardFlipGamble] result: win serverValue=${response.serverChosenValue ?? "?"} newWin=${response.newWinAmountMinor}`);

    await this.revealServerChoice(response);

    this.panel.setResultOutcome("win");

    const formattedWin = this.formatter.formatMinor(response.newWinAmountMinor);
    this.panel.setCurrentWin(formattedWin);

    await this.runner.sleep(this.resultHoldMs / 1000);

    this.panel.setResultOutcome("none");
    this.panel.setResultVisible(false);
    this.panel.cardFlipView.resetToBack();
  }

  public async presentGambleLost(response: GambleResponse): Promise<void> {
    this.log(`[CardFlipGamble] result: lose serverValue=${response.serverChosenValue ?? "?"}`);

    await this.revealServerChoice(response);

    this.panel.setResultOutcome("lose");

    const formattedWin = this.formatter.formatMinor(response.newWinAmountMinor);
    this.panel.setCurrentWin(formattedWin);

    await this.runner.sleep(this.resultHoldMs / 1000);

    this.panel.setResultOutcome("none");
    this.panel.setResultVisible(false);
    this.panel.cardFlipView.resetToBack();
  }

  /**
   * Hides both outcome titles, resets the card to back-facing, shows the reveal area, then awaits
   * the flip to the server's chosen value.
   */
  private async revealServerChoice(response: GambleResponse): Promise<void> {
    this.panel.setResultOutcome("none");
    this.panel.cardFlipView.resetToBack();
    this.panel.setResultVisible(true);
    await this.panel.cardFlipView.flipTo(response.serverChosenValue);
  }

  public async dismissGamble(): Promise<void> {
    this.log("[CardFlipGamble] dismiss");
    this.unbindInput();
    this.panel.setResultOutcome("none");
    this.panel.cardFlipView.resetToBack();
    this.panel.hide();
  }

  private bindInput(details: GambleOfferDetails): void {
    const collectNode = this.panel.collectButton;
    const handler = (): void => this.choose("collect");
    this.collectHandler = handler;
    collectNode.on("pointertap", handler);

    const container = this.panel.optionsContainer;
    for (let i = 0; i < details.options.length && i < container.children.length; i++) {
      const optionNode = container.children[i];
      const optionId = details.options[i].optionId;
      const optionHandler = (): void => this.choose(optionId);
      optionNode.on("pointertap", optionHandler);
      this.optionHandlers.push({ node: optionNode, handler: optionHandler });
    }

    this.pressBinding.bindKeys(handler);
  }

  private unbindInput(): void {
    if (this.collectHandler) {
      this.panel.collectButton.off("pointertap", this.collectHandler);
    }

    for (const { node, handler } of this.optionHandlers) {
      node.off("pointertap", handler);
    }
    this.optionHandlers = [];

    this.pressBinding.unbindAll();
    this.collectHandler = null;
  }

  private choose(choice: "collect" | string): void {
    if (!this.armed) return;
    const resolve = this.resolveChoice;
    this.resolveChoice = null;
    resolve?.(choice);
  }

  /** Resolved by the first armed press: the collect button or a press key → "collect", an option button → its id. */
  private waitForChoice(): Promise<"collect" | string> {
    return new Promise<string>((resolve) => {
      this.resolveChoice = resolve;
    });
  }
}
